import {
  type IAIReceptionistCustomQA,
  type IAIReceptionistCustomSection,
  type IAIReceptionistKnowledgeBase,
  type IImportExistingContent,
  type IImportExistingCustomSection,
  type IImportExistingQaPair,
  type IImportProposal,
  type IImportProposedCustomSection,
  type IImportProposedQaPair,
  IMPORT_EXISTING_CONTENT_MAX_CHARS,
  IMPORT_EXISTING_CUSTOM_SECTION_CONTENT_MAX_LENGTH,
  IMPORT_EXISTING_QA_PAIR_ID_MAX_LENGTH,
  IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS,
  KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS,
  KNOWLEDGE_BASE_MAX_CUSTOM_SECTION_TITLE_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH,
} from '@principle-theorem/principle-core/interfaces';
import { compact, last, omit, sum, takeWhile } from 'lodash';
import { ImportProposal } from './import-proposal';
import { ImportProposalCaps } from './import-proposal-caps';
import { ImportProposalMatching } from './import-proposal-matching';

/**
 * How much existing content fits within `IMPORT_EXISTING_CONTENT_MAX_CHARS`,
 * as counts of items in the order the extractor reads them: Q&A pairs by
 * section, then custom sections by position.
 */
interface IExistingContentBudget {
  /** The items sent at all, with their question or title. */
  sentCount: number;
  /** The sent items that also keep their answer or content. */
  withTextCount: number;
}

/**
 * Owns the existing Knowledge Base content a Website Import request sends,
 * and which references to it a proposal may keep, so what is sent and what a
 * reference may name change together.
 */
export class ImportExistingContent {
  /**
   * Describes what the Knowledge Base already covers for a Website Import
   * request, so the extractor can offer an update to an existing Q&A pair or
   * custom section only when the website says something it does not, instead
   * of proposing it again in other words. Ids, questions, answers, titles and
   * content are sent within the bounds the request accepts. A pair whose id
   * or question is outside them is left out, as a proposal names it by its
   * id, while an answer, and a custom section's title and content, are
   * shortened at a word boundary, as their opening still informs the
   * extractor and a custom section is named by its position. When the text
   * as a whole would pass `IMPORT_EXISTING_CONTENT_MAX_CHARS`, the last
   * answers and content are sent blank.
   */
  static existingContentOf(
    knowledgeBase: IAIReceptionistKnowledgeBase | undefined
  ): IImportExistingContent {
    return ImportExistingContent._fitExistingContentBudget({
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        knowledgeBase?.sectionQaPairs,
        (pairs) => ImportExistingContent._toExistingQaPairs(pairs)
      ),
      customSections: (knowledgeBase?.customSections ?? [])
        .slice(0, KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS)
        .map((section) =>
          ImportExistingContent._toExistingCustomSection(section)
        ),
    });
  }

  /**
   * Keeps a proposed item's reference to an existing Q&A pair or custom
   * section only when it names one sent in the request's existing content,
   * and only on the first item naming it, so the review never offers two
   * updates to one existing item. The extractor's output is untrusted, so an
   * invented reference is dropped and its item is proposed as a new one.
   */
  static validateExistingRefs(
    proposal: IImportProposal,
    existing: IImportExistingContent
  ): IImportProposal {
    return {
      ...proposal,
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        proposal.sectionQaPairs,
        (pairs, key) =>
          ImportExistingContent._validateQaPairRefs(
            pairs,
            existing.sectionQaPairs[key] ?? []
          )
      ),
      customSections: ImportExistingContent._validateCustomSectionRefs(
        proposal.customSections,
        existing.customSections.length
      ),
    };
  }

  /**
   * A pair the request would reject is left out rather than shortened, since
   * a shortened question could ask something else.
   */
  private static _toExistingQaPairs(
    pairs: IAIReceptionistCustomQA[]
  ): IImportExistingQaPair[] {
    return compact(
      pairs.map((pair) => ImportExistingContent._toExistingQaPair(pair))
    ).slice(0, KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS);
  }

  private static _toExistingQaPair(
    pair: IAIReceptionistCustomQA
  ): IImportExistingQaPair | undefined {
    const question = pair.question.trim();
    const isWithinRequestBounds =
      pair.id.trim() !== '' &&
      pair.id.length <= IMPORT_EXISTING_QA_PAIR_ID_MAX_LENGTH &&
      question !== '' &&
      question.length <= KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH;
    if (!isWithinRequestBounds) {
      return undefined;
    }
    return {
      id: pair.id,
      question,
      answer: ImportProposalCaps.clampTextAtWordBoundary(
        pair.answer,
        KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH
      ),
    };
  }

  private static _toExistingCustomSection(
    section: IAIReceptionistCustomSection
  ): IImportExistingCustomSection {
    return {
      title: ImportProposalCaps.clampTextAtWordBoundary(
        section.title,
        KNOWLEDGE_BASE_MAX_CUSTOM_SECTION_TITLE_LENGTH
      ),
      content: ImportProposalCaps.clampTextAtWordBoundary(
        section.content,
        IMPORT_EXISTING_CUSTOM_SECTION_CONTENT_MAX_LENGTH
      ),
    };
  }

  /**
   * Keeps the existing content within `IMPORT_EXISTING_CONTENT_MAX_CHARS` by
   * giving up what comes last in the order the extractor reads it: Q&A pairs
   * by section, then custom sections by position. Questions and titles are
   * kept before any answer or content, as without one the extractor could
   * propose a duplicate, so items are left out only when the questions and
   * titles alone would pass the budget. Answers and content then fill what
   * is left, and every one from the first that does not fit is sent blank.
   * Custom sections come last, so leaving items out only ever shortens their
   * list and no section changes position.
   */
  private static _fitExistingContentBudget(
    existing: IImportExistingContent
  ): IImportExistingContent {
    const pairLists = IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS.map(
      (key) => existing.sectionQaPairs[key] ?? []
    );
    const pairs = pairLists.flat();
    const labelLengths = [
      ...pairs.map((pair) => pair.question.length),
      ...existing.customSections.map((section) => section.title.length),
    ];
    const sentCount = ImportExistingContent._countWithinBudget(
      labelLengths,
      IMPORT_EXISTING_CONTENT_MAX_CHARS
    );
    const textLengths = [
      ...pairs.map((pair) => pair.answer.length),
      ...existing.customSections.map((section) => section.content.length),
    ];
    const budget: IExistingContentBudget = {
      sentCount,
      withTextCount: ImportExistingContent._countWithinBudget(
        textLengths.slice(0, sentCount),
        IMPORT_EXISTING_CONTENT_MAX_CHARS -
          sum(labelLengths.slice(0, sentCount))
      ),
    };
    const sectionFirstPositions = [
      0,
      ...ImportExistingContent._runningTotals(
        pairLists.map((list) => list.length)
      ),
    ];
    return {
      sectionQaPairs: ImportProposal.mapSectionQaPairs(
        existing.sectionQaPairs,
        (sectionPairs, key) =>
          ImportExistingContent._fitWithinBudget(
            sectionPairs,
            sectionFirstPositions[
              IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS.indexOf(key)
            ],
            budget,
            (pair) => ({ ...pair, answer: '' })
          )
      ),
      customSections: ImportExistingContent._fitWithinBudget(
        existing.customSections,
        pairs.length,
        budget,
        (section) => ({ ...section, content: '' })
      ),
    };
  }

  /**
   * Sends `items`, the first of which is at `firstPosition` in the
   * extractor's reading order, as `budget` allows: an item at or past
   * `sentCount` is left out, and one at or past `withTextCount` is sent
   * through `blankText`.
   */
  private static _fitWithinBudget<Item>(
    items: Item[],
    firstPosition: number,
    budget: IExistingContentBudget,
    blankText: (item: Item) => Item
  ): Item[] {
    return items
      .slice(0, Math.max(0, budget.sentCount - firstPosition))
      .map((item, index) =>
        firstPosition + index < budget.withTextCount ? item : blankText(item)
      );
  }

  /**
   * Counts how many of `lengths`, taken in order, fit within `budget`
   * together. A length is never negative, so once the running total passes
   * the budget, it stays past it.
   */
  private static _countWithinBudget(lengths: number[], budget: number): number {
    return takeWhile(
      ImportExistingContent._runningTotals(lengths),
      (total) => total <= budget
    ).length;
  }

  private static _runningTotals(values: number[]): number[] {
    return values.reduce<number[]>((totals, value) => {
      totals.push((last(totals) ?? 0) + value);
      return totals;
    }, []);
  }

  private static _validateQaPairRefs(
    pairs: IImportProposedQaPair[],
    existingPairs: IImportExistingQaPair[]
  ): IImportProposedQaPair[] {
    const existingIds = new Set(existingPairs.map((pair) => pair.id));
    const keepsRef = ImportProposalMatching.keepsRef(
      pairs.map((pair) => pair.existingId),
      (existingId) => existingIds.has(existingId)
    );
    return pairs.map((pair, index) =>
      keepsRef[index] ? pair : omit(pair, 'existingId')
    );
  }

  /**
   * The Q&A pairs inside a custom section are never sent as existing content,
   * so a reference on one of them cannot name anything that was sent.
   */
  private static _validateCustomSectionRefs(
    sections: IImportProposedCustomSection[],
    existingSectionCount: number
  ): IImportProposedCustomSection[] {
    const keepsRef = ImportProposalMatching.keepsRef(
      sections.map((section) => section.existingIndex),
      (existingIndex) =>
        ImportProposalMatching.isIndexInRange(
          existingIndex,
          existingSectionCount
        )
    );
    return sections.map((section, index) => {
      const validated = keepsRef[index]
        ? section
        : omit(section, 'existingIndex');
      if (!section.qa) {
        return validated;
      }
      return {
        ...validated,
        qa: section.qa.map((pair) => omit(pair, 'existingId')),
      };
    });
  }
}
